import { eventbus, security, ui } from '@beelday/common'
import { Config } from 'common/config'
import ConnectionHealthMonitor from 'connectivity/connection-health-monitor'
import { EventSourceManager } from 'event-source-manager/event-source-manager'
import { FC, useCallback, useContext, useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'app-redux'
import {
	Route,
	RouteProps,
	BrowserRouter as Router,
	Switch,
	useParams,
} from 'react-router-dom'
import { fetchSessionByLiveSessionId, selectTraining } from 'session-info/redux'
import { setToken } from 'session/redux'
import ExitToSessionPlanning from 'workflow/exit-to-session-planning'
import { JoinableWorkflow } from 'workflow/joinable-workflow'
import PreparationScreen from 'workflow/presentation/preparation-screen'
import PreparationScreenInPerson from 'workflow/presentation/preparation-screen-in-person'
import {
	ProvidePresenterConsole,
	usePresenterConsole,
} from 'presenter-mode/presenter-console'
import { ProjectorRoot } from 'presenter-mode/projector-root'
import type { Relay } from 'presenter-mode/replication/relay'
import { WINDOW_VIEW } from 'presenter-mode/window-role'
import { setWorkflowId } from 'workflow/redux'
import { ReactAudioContext } from './features/user-engagement/audio-context-provider'

/**
 * How long the entry gate waits for session info before choosing without it.
 * Long enough that a healthy backend always wins the race, short enough that a
 * sick one does not lock anybody out.
 */
const GATE_DECISION_TIMEOUT_MS = 8000

const WorkflowRoute = () => {
	const { initAudioContext } = useContext(ReactAudioContext)
	const { workflowId } = useParams<{ workflowId: string }>()
	const dispatch = useDispatch()
	const training = useSelector(state => selectTraining(state.sessionInfo))

	const [entered, setEntered] = useState(false)
	const presenter = usePresenterConsole()
	/**
	 * Whether the session fetch below has come back, either way. Which entry
	 * gate to show depends on its answer, and the online one mounts
	 * `HardwareCheck`, which asks for a camera and a microphone the moment it
	 * renders. Choosing before the answer arrives therefore fires a permission
	 * prompt in every in-person session - the one thing the whole feature
	 * promises never happens - and then replaces the screen.
	 */
	const [sessionSettled, setSessionSettled] = useState(false)

	const onEnter = () => {
		// Due to privacy concerns Chrome limits creating new AudioContext to event handlers only.
		// This is the only button user is forced to click in live-session therefore the only chance
		// to initialize the Web Audio API.
		try {
			initAudioContext()
		} catch (e) {}
		setEntered(true)
	}

	useEffect(() => {
		let alive = true
		const settle = () => {
			// Settled, not loaded: a session that cannot be fetched still has to
			// be enterable, and falls through to the online gate exactly as it
			// did before.
			if (alive) {
				setSessionSettled(true)
			}
		}

		/*
		 * The gate is a convenience, never a dependency. `fetchSessionByLiveSessionId`
		 * is a bare `fetch` with no timeout (`session-info/api/training-api.ts`),
		 * so a session-planning that accepts the connection and then stalls would
		 * otherwise keep every user - online included - behind a spinner and out
		 * of a room the live backend is perfectly willing to admit them to.
		 */
		const timeout = window.setTimeout(settle, GATE_DECISION_TIMEOUT_MS)

		Promise.resolve(dispatch(fetchSessionByLiveSessionId(workflowId)))
			.catch(() => undefined)
			.finally(settle)

		return () => {
			alive = false
			window.clearTimeout(timeout)
		}
	}, [dispatch, workflowId])

	useEffect(() => {
		if (entered) {
			dispatch(setWorkflowId(workflowId))
		}
	}, [dispatch, entered, workflowId])

	if (!workflowId) {
		return null
	}

	if (!entered) {
		if (!sessionSettled) {
			return <ui.FullScreenSpinner />
		}

		/*
		 * There is no interaction scheme yet, so the mode cannot come from
		 * `session-mode/` - this is the one place that has to read the session.
		 *
		 * `currentChunkSessionType`, not `sessionType`: a blended training is
		 * typed ONLINE at the training level while the chunk actually running is
		 * IN_PERSON, and the training-level answer would put the device check in
		 * front of a room full of people who have no cameras to check.
		 */
		return training?.currentChunkSessionType === 'IN_PERSON' ? (
			<PreparationScreenInPerson
				onEnter={onEnter}
				onEnterAndPresent={
					presenter?.canPresentInOneClick
						? () => {
								onEnter()
								// Same click: the window opens on the projector.
								presenter.present()
						  }
						: undefined
				}
			/>
		) : (
			<PreparationScreen onEnter={onEnter} />
		)
	}

	return <JoinableWorkflow />
}

const ProvideToken = () => {
	const token = security.useToken()
	const dispatch = useDispatch()
	useEffect(() => {
		dispatch(setToken(token))
	}, [dispatch, token])

	return null
}

const ProtectedRoute: FC<RouteProps> = ({ children, ...rest }) => {
	const token = security.useToken()
	return (
		<Route
			{...rest}
			render={() =>
				token.type === 'VALID_TOKENS' ? (
					children
				) : token.type === 'INVALID_TOKENS' ? (
					(window.location.href = '/account/login')
				) : (
					<ui.FullScreenSpinner />
				)
			}
		/>
	)
}

const authConfig = {
	clientId: Config.AUTH_CLIENT_ID,
	authUrl: Config.AUTH_URL,
	authRealm: Config.AUTH_REALM,
}

type Props = {
	/** The store's relay to presenter mode's presentation window. */
	relay: Relay | null
}

export const RoutedApp: FC<Props> = ({ relay }) => {
	const dispatch = useDispatch()
	const handleBusEvent = useCallback(
		event => dispatch({ type: `EVENTBUS/${event.type}`, payload: event }),
		[dispatch]
	)
	return (
		<security.ProvideAuth config={authConfig}>
			<ProvideToken />
			{/* The presentation window of presenter mode gets everything from the
			    trainer's main window: no eventbus socket, no event streams, no
			    joining (`presenter-mode/projector-root.tsx`). */}
			<eventbus.ConnectEventBus
				eventHandler={handleBusEvent}
				connect={WINDOW_VIEW.role !== 'projector'}
			>
				<Router>
					<Switch>
						<Route path="/404" component={ui.NotFound} />
						<ProtectedRoute path="/:workflowId">
							{WINDOW_VIEW.role === 'projector' ? (
								<ProjectorRoot presentation={WINDOW_VIEW.presentation} />
							) : (
								<ProvidePresenterConsole relay={relay}>
									<WorkflowRoute />
									<EventSourceManager />
								</ProvidePresenterConsole>
							)}
							<ConnectionHealthMonitor />
						</ProtectedRoute>
						<ProtectedRoute exact path="/">
							<ExitToSessionPlanning />
						</ProtectedRoute>
					</Switch>
				</Router>
			</eventbus.ConnectEventBus>
		</security.ProvideAuth>
	)
}
